// Executive dashboard analytics.
//
// Source-of-truth rules (see docs/canada/CEO_DASHBOARD_REVIEW.md):
// - Every per-person value comes from GET /api/cvi/periods/:id/executive, which returns the
//   latest FINAL snapshot for a locked period and the live server calculation otherwise.
// - The browser never calculates a CVI. Organization and unit values are the mean of the
//   per-person server results that exist; missing results are counted and reported, never
//   averaged as zero.
// - Indicator achievement is the engine's value (formula template, cap, approved data only).

const toNumber = value => (value == null || value === "" || !Number.isFinite(Number(value)) ? null : Number(value));

// Display thresholds already used across CVI dashboards (performance bands 80 / 60 and
// indicator bands 85 / 65). They classify results for presentation only.
export const PERFORMANCE_BANDS = { high: 80, acceptable: 60 };
export const INDICATOR_BANDS = { onTrack: 85, watch: 65 };
// A change of at least this many CVI points between two periods is surfaced for review.
export const NOTABLE_CHANGE = 5;
// Units with fewer results than this are shown but not ranked or flagged: a mean of one or
// two people is not a reliable signal for an executive comparison.
export const MIN_COMPARABLE_RESULTS = 3;
// A provisional period is only compared with a finalized one once most of its configured
// assessment weight has been evaluated (the engine's assessmentCoverage). Before that, the
// provisional mean reflects which components happen to be assessed so far, not performance,
// and a comparison would read as a decline that does not exist.
export const MIN_COMPARABLE_COVERAGE = 80;

export function comparisonReliable(state, summary) {
    if (state === "final") return true;
    return summary?.coverage != null && summary.coverage >= MIN_COMPARABLE_COVERAGE;
}

export function meanOf(values = []) {
    const available = values.map(toNumber).filter(value => value != null);
    return { value: available.length ? available.reduce((sum, value) => sum + value, 0) / available.length : null, count: available.length };
}

export function delta(current, previous) {
    const a = toNumber(current);
    const b = toNumber(previous);
    return a == null || b == null ? null : a - b;
}

const STATUS_KEYS = ["final", "provisional", "partial", "not_assessed"];

export function summarizePeople(people = []) {
    const counts = Object.fromEntries(STATUS_KEYS.map(key => [key, 0]));
    for (const person of people) counts[STATUS_KEYS.includes(person?.status) ? person.status : "not_assessed"] += 1;
    const pick = path => people.map(person => path(person));
    const cvi = meanOf(pick(p => p?.scores?.cvi));
    const individual = meanOf(pick(p => p?.scores?.individual));
    const collective = meanOf(pick(p => p?.scores?.organizational));
    const coverage = meanOf(pick(p => p?.coverage?.overall));
    return {
        total: people.length,
        counts,
        withCvi: cvi.count,
        cvi: cvi.value,
        individual: individual.value,
        individualCount: individual.count,
        collective: collective.value,
        collectiveCount: collective.count,
        coverage: coverage.value,
        coverageCount: coverage.count,
        components: {
            routine: meanOf(pick(p => p?.scores?.routine)),
            nonRoutine: meanOf(pick(p => p?.scores?.nonRoutine)),
            individualGoals: meanOf(pick(p => p?.scores?.individualGoals)),
            peerFeedback: meanOf(pick(p => p?.scores?.peerFeedback)),
            team: meanOf(pick(p => p?.collective?.team)),
            upstream: meanOf(pick(p => p?.collective?.upstream)),
            organization: meanOf(pick(p => p?.collective?.organization)),
        },
    };
}

export function performanceDistribution(people = []) {
    const scores = people.map(p => toNumber(p?.scores?.cvi)).filter(value => value != null);
    return {
        high: scores.filter(s => s >= PERFORMANCE_BANDS.high).length,
        acceptable: scores.filter(s => s >= PERFORMANCE_BANDS.acceptable && s < PERFORMANCE_BANDS.high).length,
        attention: scores.filter(s => s < PERFORMANCE_BANDS.acceptable).length,
        total: scores.length,
    };
}

export function periodState(period) {
    const status = String(period?.status || "").toLowerCase();
    if (status === "locked") return "final";
    if (status === "draft") return "draft";
    return "provisional";
}

// One entry per period, ordered by start date. Failed or empty periods stay in the list
// (so the axis is honest) with null values; the chart breaks the line instead of joining.
export function buildExecutiveTrend(entries = []) {
    return entries
        .filter(entry => entry?.period)
        .map((entry, index) => {
            const summary = entry.payload ? summarizePeople(entry.payload.people || []) : null;
            return {
                index,
                periodId: entry.period.id,
                label: entry.period.label || entry.payload?.period?.name || "",
                // The period list start (always present) orders the axis, even when a request failed.
                start: String(entry.period.start || entry.period.startDate || entry.payload?.period?.startDate || ""),
                state: periodState(entry.payload?.period || entry.period),
                failed: Boolean(entry.error),
                loaded: Boolean(entry.payload) || Boolean(entry.error),
                cvi: summary?.cvi ?? null,
                individual: summary?.individual ?? null,
                collective: summary?.collective ?? null,
                withCvi: summary?.withCvi ?? 0,
                total: summary?.total ?? 0,
            };
        })
        .sort((a, b) => (a.start && b.start && a.start !== b.start ? a.start.localeCompare(b.start) : a.index - b.index));
}

// Most recent finalized period before the selected one that has an organization CVI.
export function previousFinalized(trend = [], periodId) {
    const position = trend.findIndex(item => String(item.periodId) === String(periodId));
    const before = position < 0 ? trend : trend.slice(0, position);
    for (let i = before.length - 1; i >= 0; i -= 1) {
        if (before[i].state === "final" && before[i].cvi != null) return before[i];
    }
    return null;
}

export function subtreeIds(orgUnits = [], rootId) {
    const ids = new Set([rootId]);
    let changed = true;
    while (changed) {
        changed = false;
        for (const unit of orgUnits) {
            if (unit.parentId && ids.has(unit.parentId) && !ids.has(unit.id)) { ids.add(unit.id); changed = true; }
        }
    }
    return ids;
}

// Top-level organizational units (flexible org) or departments (legacy structure).
export function topLevelUnits({ orgUnits = [], departments = [] }) {
    const active = orgUnits.filter(unit => unit.isActive !== false);
    if (active.length) {
        return active.filter(unit => !unit.parentId).map(unit => {
            const ids = subtreeIds(active, unit.id);
            return {
                id: unit.id, label: unit.label || unit.name, mode: "org-unit", drillable: true,
                childCount: active.filter(child => child.parentId === unit.id).length,
                includes: person => ids.has(person?.user?.orgUnitId),
            };
        });
    }
    return departments.map(dept => ({
        id: dept.id, label: dept.label || dept.name, mode: "department", drillable: false, childCount: 0,
        includes: person => person?.user?.departmentId === dept.id,
    }));
}

export function unitBreakdown({ units = [], people = [], previousPeople = null }) {
    return units.map(unit => {
        const members = people.filter(unit.includes);
        const summary = summarizePeople(members);
        const previous = previousPeople ? summarizePeople(previousPeople.filter(unit.includes)) : null;
        const comparable = summary.withCvi >= MIN_COMPARABLE_RESULTS;
        const previousComparable = previous ? previous.withCvi >= MIN_COMPARABLE_RESULTS : false;
        return {
            id: unit.id,
            label: unit.label,
            drillable: unit.drillable,
            childCount: unit.childCount,
            people: summary.total,
            withCvi: summary.withCvi,
            cvi: summary.cvi,
            individual: summary.individual,
            collective: summary.collective,
            comparable,
            previousCvi: previous?.cvi ?? null,
            change: comparable && previousComparable ? delta(summary.cvi, previous.cvi) : null,
        };
    }).sort((a, b) => {
        if (a.cvi == null && b.cvi == null) return String(a.label).localeCompare(String(b.label));
        if (a.cvi == null) return 1;
        if (b.cvi == null) return -1;
        return b.cvi - a.cvi;
    });
}

export function indicatorStatus(indicator) {
    const achievement = toNumber(indicator?.achievement);
    if (achievement == null) return "no_data";
    if (achievement < INDICATOR_BANDS.watch) return "critical";
    if (achievement < INDICATOR_BANDS.onTrack) return "watch";
    return "on_track";
}

const INDICATOR_ORDER = { critical: 0, watch: 1, no_data: 2, on_track: 3 };
export function rankIndicators(indicators = []) {
    return indicators
        .map(indicator => ({ ...indicator, status: indicatorStatus(indicator) }))
        .sort((a, b) => (INDICATOR_ORDER[a.status] - INDICATOR_ORDER[b.status])
            || ((toNumber(a.achievement) ?? 101) - (toNumber(b.achievement) ?? 101))
            || String(a.name || "").localeCompare(String(b.name || "")));
}

export function indicatorCounts(indicators = []) {
    const counts = { critical: 0, watch: 0, on_track: 0, no_data: 0, total: indicators.length };
    for (const indicator of indicators) counts[indicatorStatus(indicator)] += 1;
    return counts;
}

const SEVERITY_ORDER = { critical: 0, warning: 1, info: 2 };

// Prioritized items for executive review. Each item is factual (a count or a measured
// difference) and links to where it can be examined; none implies a cause.
export function buildAttentionItems({ state, summary, units = [], indicators = [], orgChange = null, pendingDecisions = 0, failed = false, comparable = true }) {
    const items = [];
    if (failed) {
        items.push({ id: "load-failed", severity: "critical", kind: "load_failed" });
        return items;
    }
    const ranked = rankIndicators(indicators);
    const critical = ranked.filter(item => item.status === "critical");
    if (critical.length) items.push({ id: "kpi-critical", severity: "critical", kind: "kpi_critical", count: critical.length, names: critical.slice(0, 3).map(item => item.name), target: "indicators" });
    if (!comparable && state !== "final" && summary?.total) items.push({ id: "provisional-incomplete", severity: "info", kind: "provisional_incomplete", value: summary.coverage, target: "coverage" });
    if (comparable && orgChange != null && orgChange <= -NOTABLE_CHANGE) items.push({ id: "org-decline", severity: "warning", kind: "org_decline", value: orgChange, target: "trend" });
    for (const unit of comparable ? units : []) {
        if (unit.change != null && unit.change <= -NOTABLE_CHANGE) {
            items.push({ id: `unit-decline-${unit.id}`, severity: "warning", kind: "unit_decline", unit: unit.label, unitId: unit.id, value: unit.change, target: "units" });
        } else if (unit.comparable && unit.cvi != null && unit.cvi < PERFORMANCE_BANDS.acceptable) {
            items.push({ id: `unit-low-${unit.id}`, severity: "warning", kind: "unit_low", unit: unit.label, unitId: unit.id, value: unit.cvi, target: "units" });
        }
    }
    if (summary && summary.total) {
        const missing = summary.total - summary.withCvi;
        if (state !== "final" && missing > 0) items.push({ id: "coverage", severity: "warning", kind: "coverage_gap", count: missing, total: summary.total, target: "coverage" });
        if (state === "final" && missing > 0) items.push({ id: "no-final", severity: "info", kind: "no_final_result", count: missing, total: summary.total, target: "coverage" });
    }
    const noData = ranked.filter(item => item.status === "no_data");
    if (noData.length) items.push({ id: "kpi-no-data", severity: "info", kind: "kpi_no_data", count: noData.length, target: "indicators" });
    const watch = ranked.filter(item => item.status === "watch");
    if (watch.length) items.push({ id: "kpi-watch", severity: "info", kind: "kpi_watch", count: watch.length, target: "indicators" });
    if (pendingDecisions > 0) items.push({ id: "decisions", severity: "info", kind: "pending_decisions", count: pendingDecisions, target: "inbox" });
    if (comparable && orgChange != null && orgChange >= NOTABLE_CHANGE) items.push({ id: "org-improve", severity: "info", kind: "org_improve", value: orgChange, target: "trend" });
    return items.sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]);
}
